import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { trackEvent, trackPageView } from './lib/telemetry';
import SeoHead, { SeoProps } from './components/SeoHead';
import { rememberAttributionFromUrl } from './lib/attribution';
import { installPublicInteractionTelemetry } from './lib/publicInteractionTelemetry';
import PageLoadFallback from './components/PageLoadFallback';

const HomePage = lazy(() => import('./pages/HomePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const InfoPage = lazy(() => import('./pages/InfoPage'));
const SuccessPage = lazy(() => import('./pages/SuccessPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const InstallPage = lazy(() => import('./pages/InstallPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PressPage = lazy(() => import('./pages/PressPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const StrategicPage = lazy(() => import('./pages/StrategicPage'));
const MissionPage = lazy(() => import('./pages/MissionPage'));
const TutorialsPage = lazy(() => import('./pages/tutorials/TutorialsPage'));
const QuickstartEngineerPage = lazy(() => import('./pages/tutorials/QuickstartEngineerPage'));
const QuickstartBeginnerPage = lazy(() => import('./pages/tutorials/QuickstartBeginnerPage'));
const SetupClaudeCodePage = lazy(() => import('./pages/tutorials/SetupClaudeCodePage'));
const SetupOpenClawPage = lazy(() => import('./pages/tutorials/SetupOpenClawPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ForAgentsPage = lazy(() => import('./pages/ForAgentsPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const LicensePage = lazy(() => import('./pages/legal/LicensePage'));
const LegalEulaPage = lazy(() => import('./pages/legal/EulaPage'));
const CommercialLegalPage = lazy(() => import('./pages/legal/CommercialPage'));
const TrademarkPage = lazy(() => import('./pages/legal/TrademarkPage'));
const LegalPrivacyPage = lazy(() => import('./pages/legal/NewPrivacyPage'));
const BillingPolicyPage = lazy(() => import('./pages/legal/BillingPolicyPage'));
const LandingPage001B = lazy(() => import('./pages/LandingPage001B'));
const LandingRouterPage = lazy(() => import('./pages/landing'));
const YouTubePromoRedirect = lazy(() => import('./pages/landing/PromoFunnelRedirect').then((module) => ({ default: module.YouTubePromoRedirect })));
const XPromoRedirect = lazy(() => import('./pages/landing/PromoFunnelRedirect').then((module) => ({ default: module.XPromoRedirect })));
const XPromo2026Redirect = lazy(() => import('./pages/landing/PromoFunnelRedirect').then((module) => ({ default: module.XPromo2026Redirect })));

const LandingOrPublicProfileRoute: React.FC = () => {
  const { slug = '' } = useParams();
  return slug.startsWith('@') ? <PublicProfilePage /> : <LandingRouterPage />;
};

const softwareStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'agentOmatic',
  applicationCategory: 'DeveloperApplication',
  operatingSystem: 'Linux, Windows, macOS',
  description: 'Desktop software for orchestrating and managing multiple AI coding agents in parallel across app, SaaS, and game development workflows including Godot, Unreal Engine, Unity, Phaser, PixiJS, PlayCanvas, Babylon.js, melonJS, Panda3D, Bevy, Defold, and SFML.',
  url: 'https://agentomatic.io/',
  brand: {
    '@type': 'Brand',
    name: 'agentOmatic',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Zitotron Tech LLC',
    url: 'https://zitotrontech.com',
  },
};

function getSeoForPath(pathname: string): SeoProps {
  const publicPages: Record<string, SeoProps> = {
    '/': {
      title: 'agentOmatic — Agentic Development Environment',
      description: 'agentOmatic helps builders run multiple AI coding agents in parallel to ship games, AI MVPs, and SaaS products in under 30 days. Optimized for Godot, Unreal, Unity, and HTML5 stacks. The ultimate workspace for game devs, startup founders, and solopreneurs.',
      keywords: 'agentOmatic, AI coding agents, game development, game dev, AI startup founders, startup MVP, solopreneur, indie hacker, ship in 30 days, Godot, Unreal Engine, Unity, Phaser, PixiJS, PlayCanvas, Babylon.js, melonJS, Panda3D, Bevy, Defold, SFML, Steam games, AAA games, HTML5 games, browser games, Claude Code, Codex CLI, OpenCode, Antigravity CLI, Gemini CLI, Gemini 3.5, Grok Build CLI, SuperGrok Heavy, Ollama',
      path: '/',
      structuredData: softwareStructuredData,
    },
    '/pricing': {
      title: 'Pricing',
      description: 'agentOmatic pricing and plan limits across Community, Community-Agents, Solo, Founder, Startup, and Enterprise tiers.',
      path: '/pricing',
      structuredData: softwareStructuredData,
    },
    '/features': {
      title: 'Features',
      description: 'agentOmatic feature overview for running, directing, and recovering multiple AI coding agents in one desktop workspace.',
      path: '/features',
      structuredData: softwareStructuredData,
    },
    '/learn-more': {
      title: 'Learn More',
      description: 'Background information about agentOmatic, the Agentic Development Environment category, compatibility, workflow, and FAQs.',
      path: '/learn-more',
      structuredData: softwareStructuredData,
    },
    '/download': {
      title: 'Download agentOmatic',
      description: 'Download the latest agentOmatic desktop installers for Windows, macOS, and Linux, including checksums and install guidance.',
      keywords: 'download agentOmatic, agentOmatic Windows installer, agentOmatic macOS dmg, agentOmatic Linux deb, agentOmatic Linux rpm, AI coding agent desktop app',
      path: '/download',
      structuredData: softwareStructuredData,
    },
    '/for-agents': {
      title: 'For Agents',
      description: 'Agent-readable onboarding for autonomous systems, bots, and operator workflows using agentOmatic with OpenClaw, Claude Computer Use, Operator-style tools, and related agent stacks.',
      path: '/for-agents',
    },
    '/tutorials': {
      title: 'Tutorials',
      description: 'Tutorials for getting started with agentOmatic, including download, install, workspace setup, and agentic engineering workflows.',
      path: '/tutorials',
    },
    '/tutorials/quickstart-engineer': {
      title: 'Engineer Quickstart',
      description: 'Engineer quickstart for installing and launching agentOmatic with terminal-based AI coding agents.',
      path: '/tutorials/quickstart-engineer',
    },
    '/tutorials/quickstart-beginner': {
      title: 'Beginner Quickstart',
      description: 'Beginner-friendly setup guide for agentOmatic, covering installation and first-run steps for AI coding agents.',
      path: '/tutorials/quickstart-beginner',
    },
    '/tutorials/setup-claude-code': {
      title: 'Claude Code Setup',
      description: 'Setup guide for using agentOmatic with Claude Code and other CLI-based coding agents.',
      path: '/tutorials/setup-claude-code',
    },
    '/tutorials/setup-openclaw': {
      title: 'OpenClaw Setup',
      description: 'Setup guide for using agentOmatic with OpenClaw workflows and adjacent autonomous agent tooling.',
      path: '/tutorials/setup-openclaw',
    },
    '/about': {
      title: 'About',
      description: 'About agentOmatic and Zitotron Tech LLC, the team behind the Agentic Development Environment.',
      path: '/about',
    },
    '/press': {
      title: 'Press',
      description: 'Press information, product summary, and media-friendly overview for agentOmatic.',
      path: '/press',
    },
    '/services': {
      title: 'Services',
      description: 'Professional services and related offerings around agentOmatic deployment, integration, and rollout.',
      path: '/services',
    },
    '/mission': {
      title: 'Mission',
      description: 'agentOmatic mission and product direction for multi-agent software development workflows.',
      path: '/mission',
    },
    '/careers': {
      title: 'Careers',
      description: 'Open roles and hiring information for building agentOmatic and its agent-focused product surface.',
      path: '/careers',
    },
    '/legal/license': {
      title: 'License',
      description: 'Commercial license terms for agentOmatic.',
      path: '/legal/license',
    },
    '/legal/eula': {
      title: 'EULA',
      description: 'End User License Agreement for agentOmatic.',
      path: '/legal/eula',
    },
    '/legal/privacy': {
      title: 'Privacy Policy',
      description: 'Privacy policy for agentOmatic and Zitotron Tech LLC.',
      path: '/legal/privacy',
    },
    '/legal/commercial': {
      title: 'Commercial License',
      description: 'Commercial, OEM, and enterprise licensing details for agentOmatic.',
      path: '/legal/commercial',
    },
    '/legal/trademark': {
      title: 'Trademark Policy',
      description: 'Trademark policy and permitted use guidance for agentOmatic and Zitotron Tech LLC marks.',
      path: '/legal/trademark',
    },
    '/legal/billing': {
      title: 'Billing Policy',
      description: 'Billing, refund, and subscription policy for agentOmatic plans.',
      path: '/legal/billing',
    },
  };

  if (pathname === '/download-users-windows-01-a') {
    return {
      title: 'agentOmatic Windows Update',
      description: 'Windows update landing page for existing agentOmatic users.',
      path: pathname,
      robots: 'noindex,nofollow',
      structuredData: softwareStructuredData,
    };
  }

  if (pathname === '/login' || pathname === '/install' || pathname.startsWith('/@') || pathname.startsWith('/account') || pathname.startsWith('/portal') || pathname.startsWith('/guest-portal') || pathname === '/x' || pathname === '/x-learn-more-001' || pathname === '/youtube' || pathname === '/youtube-learn-more-001' || pathname.startsWith('/success') || pathname.startsWith('/checkout') || pathname.startsWith('/admin') || pathname.startsWith('/landing') || pathname.startsWith('/x-agentic-') || pathname.startsWith('/x-promo-code-') || pathname.startsWith('/x-developers-cli-') || pathname.startsWith('/youtube-promo-code-') || pathname.startsWith('/app-signups-') || pathname.startsWith('/dev-promo-') || pathname.startsWith('/download-001-')) {
    return {
      title: pathname === '/install' ? 'Install agentOmatic' : pathname.startsWith('/@') ? 'agentOmatic profile' : 'agentOmatic',
      description: pathname === '/install'
        ? 'Native command-line install and desktop sign-in handoff for agentOmatic.'
        : pathname.startsWith('/@')
          ? 'agentOmatic builder profile.'
        : 'agentOmatic account, checkout, and signup confirmation flow.',
      path: pathname,
      robots: 'noindex,nofollow',
    };
  }

  if (publicPages[pathname]) {
    return publicPages[pathname];
  }

  return {
    title: 'Page Not Found',
    description: 'The requested page could not be found on agentomatic.io.',
    path: pathname,
    robots: 'noindex,nofollow',
  };
}

const App: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    rememberAttributionFromUrl();
    let cancelled = false;
    let timeoutId: number | undefined;
    let idleId: number | undefined;

    const startReplay = () => {
      if (cancelled) return;
      window.removeEventListener('pointerdown', startReplay);
      window.removeEventListener('keydown', startReplay);
      window.removeEventListener('touchstart', startReplay);
      if (timeoutId) window.clearTimeout(timeoutId);
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      import('./lib/sessionReplay')
        .then(({ maybeStartSessionReplay, noteReplayRoute }) => {
          if (cancelled) return;
          maybeStartSessionReplay(location.pathname);
          noteReplayRoute(location.pathname);
        })
        .catch(() => {});
    };

    window.addEventListener('pointerdown', startReplay, { once: true, passive: true });
    window.addEventListener('keydown', startReplay, { once: true });
    window.addEventListener('touchstart', startReplay, { once: true, passive: true });
    timeoutId = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(startReplay, { timeout: 3000 });
      } else {
        startReplay();
      }
    }, 15000);

    return () => {
      cancelled = true;
      window.removeEventListener('pointerdown', startReplay);
      window.removeEventListener('keydown', startReplay);
      window.removeEventListener('touchstart', startReplay);
      if (timeoutId) window.clearTimeout(timeoutId);
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
    };
  }, [location.pathname, location.search]);

  useEffect(() => installPublicInteractionTelemetry(), []);

  useEffect(() => {
    const isLandingRoute = location.pathname === '/'
      || location.pathname === '/download'
      || location.pathname === '/x'
      || location.pathname === '/x-learn-more-001'
      || location.pathname === '/youtube'
      || location.pathname === '/youtube-learn-more-001'
      || location.pathname.startsWith('/landing')
      || location.pathname.startsWith('/x-agentic-')
      || location.pathname.startsWith('/x-promo-code-')
      || location.pathname.startsWith('/x-developers-cli-')
      || location.pathname.startsWith('/youtube-promo-code-')
      || location.pathname.startsWith('/app-signups-')
      || location.pathname.startsWith('/dev-promo-')
      || location.pathname.startsWith('/download-001-');
    if (isLandingRoute) return;
    trackPageView(location.pathname).catch(() => {});
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onPageHide = () => {
      trackEvent('page_exit', { exit_path: location.pathname, reason: 'pagehide' }).catch(() => {});
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/pricing') return;

    let attempts = 0;
    let timeoutId: number | undefined;

    const scrollToPricing = () => {
      const pricing = document.getElementById('pricing');
      if (pricing) {
        pricing.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < 20) {
        timeoutId = window.setTimeout(scrollToPricing, 100);
      }
    };

    requestAnimationFrame(() => {
      timeoutId = window.setTimeout(scrollToPricing, 50);
    });

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [location.pathname, location.search, location.hash]);

  const seo = getSeoForPath(location.pathname);

  return (
    <>
      <SeoHead {...seo} />
      {/* AccountChip now lives inline in each page's nav so it scales
          with the layout instead of floating in a fixed corner. */}
      <Suspense fallback={<PageLoadFallback pathname={location.pathname} />}>
      <Routes>
      <Route path="/" element={<LandingRouterPage slugOverride="home-main-2026-a" />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/learn-more" element={<InfoPage />} />
      <Route path="/download" element={<Navigate to="/portal-guest" replace />} />
      <Route path="/install" element={<InstallPage />} />
      <Route path="/download-users-windows-01-a" element={<HomePage page="download-users-windows-01-a" />} />
      <Route path="/success" element={<SuccessPage />} />
      <Route path="/success/:tier" element={<SuccessPage />} />
      <Route path="/landing/001-a" element={<Navigate to="/" replace />} />
      <Route path="/landing/001-b" element={<LandingPage001B />} />
      <Route path="/landing/:slug" element={<LandingRouterPage />} />
      <Route path="/download-001-a-xcbz" element={<LandingRouterPage slugOverride="download-001-a-xcbz" />} />
      <Route path="/download-001-a-xcbz-learn-more-001" element={<LandingRouterPage slugOverride="download-001-a-xcbz-learn-more-001" />} />
      <Route path="/download-001-b-k9v4r" element={<LandingRouterPage slugOverride="download-001-b-k9v4r" />} />
      <Route path="/download-001-b-k9v4r-learn-more-001" element={<LandingRouterPage slugOverride="download-001-b-k9v4r-learn-more-001" />} />
      <Route path="/youtube" element={<LandingRouterPage slugOverride="youtube" />} />
      <Route path="/youtube-learn-more-001" element={<LandingRouterPage slugOverride="youtube-learn-more-001" />} />
      <Route path="/youtube-cli" element={<LandingRouterPage slugOverride="youtube-cli" />} />
      <Route path="/youtube-cli-learn-more-001" element={<LandingRouterPage slugOverride="youtube-cli-learn-more-001" />} />
      <Route path="/youtube-promo-code-001-a-2026-xybc" element={<LandingRouterPage slugOverride="youtube-promo-code-001-a-2026-xybc" />} />
      <Route path="/youtube-promo-code-001-a-2026-xybc-learn-more-001" element={<LandingRouterPage slugOverride="youtube-promo-code-001-a-2026-xybc-learn-more-001" />} />
      <Route path="/youtube-promo-code-001-b-2026-xyjq" element={<LandingRouterPage slugOverride="youtube-promo-code-001-b-2026-xyjq" />} />
      <Route path="/youtube-promo-code-001-b-2026-xyjq-learn-more-001" element={<LandingRouterPage slugOverride="youtube-promo-code-001-b-2026-xyjq-learn-more-001" />} />
      <Route path="/youtube-promo-code-001-2026-xyb1" element={<YouTubePromoRedirect />} />
      <Route path="/x-promo-code-001-a-2026-q8vn" element={<LandingRouterPage slugOverride="x-promo-code-001-a-2026-q8vn" />} />
      <Route path="/x-promo-code-001-a-2026-q8vn-learn-more-001" element={<LandingRouterPage slugOverride="x-promo-code-001-a-2026-q8vn-learn-more-001" />} />
      <Route path="/x-promo-code-001-b-2026-m4pz" element={<LandingRouterPage slugOverride="x-promo-code-001-b-2026-m4pz" />} />
      <Route path="/x-promo-code-001-b-2026-m4pz-learn-more-001" element={<LandingRouterPage slugOverride="x-promo-code-001-b-2026-m4pz-learn-more-001" />} />
      <Route path="/x-promo-code-001-2026-q8x1" element={<XPromoRedirect />} />
      <Route path="/x-promo-code-2026-xybz-a" element={<LandingRouterPage slugOverride="x-promo-code-2026-xybz-a" />} />
      <Route path="/x-promo-code-2026-xybz-a-learn-more-001" element={<LandingRouterPage slugOverride="x-promo-code-2026-xybz-a-learn-more-001" />} />
      <Route path="/x-promo-code-2026-xzkj-b" element={<LandingRouterPage slugOverride="x-promo-code-2026-xzkj-b" />} />
      <Route path="/x-promo-code-2026-xzkj-b-learn-more-001" element={<LandingRouterPage slugOverride="x-promo-code-2026-xzkj-b-learn-more-001" />} />
      <Route path="/x-promo-code-2026-xybz" element={<XPromo2026Redirect />} />
      <Route path="/x-developers-cli-2026-axyz23" element={<LandingRouterPage slugOverride="x-developers-cli-2026-axyz23" />} />
      <Route path="/x-developers-cli-2026-axyz23-learn-more-001" element={<LandingRouterPage slugOverride="x-developers-cli-2026-axyz23-learn-more-001" />} />
      <Route path="/x" element={<LandingRouterPage slugOverride="x" />} />
      <Route path="/x-learn-more-001" element={<LandingRouterPage slugOverride="x-learn-more-001" />} />
      <Route path="/x-simple-2026-a" element={<Navigate to="/" replace />} />
      <Route path="/x-agentic-engineering-pro-devs-01-a" element={<LandingRouterPage slugOverride="x-agentic-engineering-pro-devs-01-a" />} />
      <Route path="/login" element={<Navigate to="/portal" replace />} />
      <Route path="/account" element={<Navigate to="/portal/account" replace />} />
      <Route path="/account/settings" element={<Navigate to="/portal/account" replace />} />
      <Route path="/guest-portal" element={<GuestPortalRedirect />} />
      <Route path="/guest-portal/:section" element={<GuestPortalRedirect />} />
      <Route path="/portal-guest" element={<AccountPage />} />
      <Route path="/portal-guest/:section" element={<AccountPage />} />
      <Route path="/portal" element={<AccountPage />} />
      <Route path="/portal/:section" element={<AccountPage />} />
      <Route path="/app-signups-direct-001-xljk" element={<LandingRouterPage slugOverride="app-signups-direct-001-xljk" />} />
      <Route path="/:slug" element={<LandingOrPublicProfileRoute />} />
      <Route path="/checkout/:tier" element={<CheckoutPage />} />
      {/* Legacy routes redirect to the canonical /legal/* pages so there's
          one source of truth (markdown rendered from repo's /legal folder). */}
      <Route path="/eula"    element={<Navigate to="/legal/eula" replace />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/terms"   element={<TermsPage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/strategic" element={<StrategicPage />} />
      <Route path="/mission" element={<MissionPage />} />
      {/* <Route path="/affiliates" element={<AffiliatesPage />} /> */}
      <Route path="/admin/analytics" element={<Navigate to="/" replace />} />
      <Route path="/tutorials" element={<TutorialsPage />} />
      <Route path="/tutorials/quickstart-engineer" element={<QuickstartEngineerPage />} />
      <Route path="/tutorials/quickstart-beginner" element={<QuickstartBeginnerPage />} />
      <Route path="/tutorials/setup-claude-code" element={<SetupClaudeCodePage />} />
      <Route path="/tutorials/setup-openclaw" element={<SetupOpenClawPage />} />
      {/* Company + legal anchors. The /legal/* routes render the source
          markdown from the repo's /legal folder so portal copy never
          drifts from the authoritative docs. */}
      <Route path="/about"            element={<AboutPage />} />
      <Route path="/for-agents"       element={<ForAgentsPage />} />
      <Route path="/agents"           element={<Navigate to="/for-agents" replace />} />
      <Route path="/bots"             element={<Navigate to="/for-agents" replace />} />
      <Route path="/careers"          element={<CareersPage />} />
      <Route path="/jobs"             element={<Navigate to="/careers" replace />} />
      <Route path="/hiring"           element={<Navigate to="/careers" replace />} />
      <Route path="/legal/license"    element={<LicensePage />} />
      <Route path="/legal/eula"       element={<LegalEulaPage />} />
      <Route path="/legal/commercial" element={<CommercialLegalPage />} />
      <Route path="/legal/trademark"  element={<TrademarkPage />} />
      <Route path="/legal/privacy"    element={<LegalPrivacyPage />} />
      <Route path="/legal/billing"    element={<BillingPolicyPage />} />
      <Route path="/refunds"          element={<Navigate to="/legal/billing" replace />} />
      <Route path="/billing-policy"   element={<Navigate to="/legal/billing" replace />} />
      {/* 404 catch-all — carries the same footer so even errors look owned. */}
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </>
  );
};

const GuestPortalRedirect: React.FC = () => {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/guest-portal/, '') || '';
  return <Navigate to={`/portal-guest${suffix}${location.search}${location.hash}`} replace />;
};

export default App;
